import { getAuth, onAuthStateChanged, signOut, updateProfile } from 'firebase/auth';
import { collection, deleteDoc, doc, getDoc, getDocs, getFirestore, orderBy, query, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FollowButton from '../Components/FollowButton';
import { app } from '../firebase';
import { createConnectOnboardingLink, refreshConnectAccountStatus, requestWithdrawal } from '../services/payments';
import { buildUserProfileDocument, makeUsername } from '../utils/profileDefaults';
import { hasPlayableAudio, normalizeTrackDocument } from '../utils/tracks';
import AuthForm from './AuthForm';
import './Profile.css';

const db = getFirestore(app);
const auth = getAuth(app);
const getTrackGenres = (track) => Array.isArray(track.genres) && track.genres.length
    ? track.genres
    : [track.genre || 'Uncategorized'];
const formatTrackPrice = (price) => {
    const numericPrice = Number(price || 0);
    return Number.isFinite(numericPrice) ? numericPrice.toFixed(2) : '0.00';
};
const formatMoney = (amount) => {
    const numericAmount = Number(amount || 0);
    return Number.isFinite(numericAmount) ? numericAmount.toFixed(2) : '0.00';
};
const getTrackArtistName = (tracks = []) => {
    const trackWithArtist = tracks.find((track) => {
        const artist = track.artist || '';
        return artist.trim() && artist.toLowerCase() !== 'unknown artist' && artist.toLowerCase() !== 'preview';
    });

    return trackWithArtist?.artist || '';
};

const profileThemeOptions = [
    { id: 'amber', label: 'Amber' },
    { id: 'olive', label: 'Olive' },
    { id: 'granite', label: 'Granite' },
];

const normalizeProfileTheme = (theme) => {
    if (theme === 'ember') return 'amber';
    if (theme === 'graphite') return 'granite';
    return profileThemeOptions.some((option) => option.id === theme) ? theme : 'amber';
};

const EditProfileModal = ({ profile, onSave, onClose, user }) => {
    const [editProfile, setEditProfile] = useState(profile);
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false);

    // Update editProfile when profile prop changes
    useEffect(() => {
        setEditProfile(profile);
        setProfilePicFile(null);
        setError('');
        setSuccess(false);
        setTouched(false);
    }, [profile]);

    // Validation helpers
    const isValidUrl = (url) => {
        if (!url) return true;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };
    const isFormValid = () => {
        return (
            editProfile.name.trim() !== '' &&
            isValidUrl(editProfile.socialLinks.twitter) &&
            isValidUrl(editProfile.socialLinks.instagram) &&
            isValidUrl(editProfile.socialLinks.facebook)
        );
    };
    // Track changes
    useEffect(() => {
        setTouched(JSON.stringify(editProfile) !== JSON.stringify(profile) || !!profilePicFile);
    }, [editProfile, profile, profilePicFile]);

    // Handle profile picture upload
    const handleProfilePicChange = (e) => {
        if (e.target.files[0]) {
            setProfilePicFile(e.target.files[0]);
            setTouched(true);
        }
    };

    const handleChange = (field, value) => {
        setEditProfile({ ...editProfile, [field]: value });
    };
    const handleSocialChange = (platform, value) => {
        setEditProfile({
            ...editProfile,
            socialLinks: { ...editProfile.socialLinks, [platform]: value },
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        let updatedProfile = { ...editProfile };
        // Upload new profile picture if selected
        if (profilePicFile && user) {
            try {
                const storageRef = ref(getStorage(app), `profilePictures/${user.uid}`);
                await uploadBytes(storageRef, profilePicFile);
                const url = await getDownloadURL(storageRef);
                updatedProfile.profilePicture = url;
            } catch {
                setError('Failed to upload profile picture.');
                setSaving(false);
                return;
            }
        }
        if (!isFormValid()) {
            setError('Please fill out all required fields and use valid URLs.');
            setSaving(false);
            return;
        }
        try {
            await onSave(updatedProfile);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2000);
        } catch (err) {
            setError('Failed to save profile.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="edit-profile-modal-overlay" onClick={onClose}>
            <div className="edit-profile-modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="edit-profile-modal-header">
                    <h2 className="edit-profile-modal-title">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        Edit Profile
                    </h2>
                    <button className="edit-profile-close-btn" onClick={onClose} aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div className="edit-profile-modal-content">
                    {/* Profile Picture Upload */}
                    <div className="edit-profile-picture-section">
                        <div className="profile-picture-preview">
                            <img
                                src={profilePicFile ? URL.createObjectURL(profilePicFile) : (editProfile.profilePicture || '/default-profile.jpg')}
                                alt="Profile Preview"
                                className="profile-picture-preview-img"
                            />
                            <div className="profile-picture-overlay">
                                <label className="profile-picture-upload-label">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleProfilePicChange}
                                        className="profile-picture-upload-input"
                                    />
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="17 8 12 3 7 8"/>
                                        <line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    <span>Change Photo</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div className="edit-profile-form">
                        <div className="form-group">
                            <label className="form-label">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                    <circle cx="12" cy="7" r="4"/>
                                </svg>
                                Display name <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-input"
                                value={editProfile.name}
                                onChange={e => handleChange('name', e.target.value)}
                                placeholder="Metro Beats"
                            />
                            <span className="form-helper">This is the name buyers see on your profile and beat cards.</span>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 9h16"/>
                                    <path d="M4 15h16"/>
                                    <path d="M10 3 8 21"/>
                                    <path d="m16 3-2 18"/>
                                </svg>
                                Public username
                            </label>
                            <input
                                type="text"
                                className="form-input"
                                value={editProfile.username || ''}
                                onChange={e => handleChange('username', e.target.value)}
                                placeholder="metro-beats"
                            />
                            <span className="form-helper">Used as your public handle. Keep it short and recognizable.</span>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                    <polyline points="10 9 9 9 8 9"/>
                                </svg>
                                Bio
                            </label>
                            <textarea
                                className="form-textarea"
                                value={editProfile.bio}
                                onChange={e => handleChange('bio', e.target.value)}
                                placeholder="Tell us about yourself..."
                                rows="4"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                                </svg>
                                Twitter URL
                            </label>
                            <input
                                type="url"
                                className="form-input"
                                placeholder="https://twitter.com/yourusername"
                                value={editProfile.socialLinks.twitter}
                                onChange={e => handleSocialChange('twitter', e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                                </svg>
                                Instagram URL
                            </label>
                            <input
                                type="url"
                                className="form-input"
                                placeholder="https://instagram.com/yourusername"
                                value={editProfile.socialLinks.instagram}
                                onChange={e => handleSocialChange('instagram', e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                                </svg>
                                Facebook URL
                            </label>
                            <input
                                type="url"
                                className="form-input"
                                placeholder="https://facebook.com/yourusername"
                                value={editProfile.socialLinks.facebook}
                                onChange={e => handleSocialChange('facebook', e.target.value)}
                            />
                        </div>

                        {error && (
                            <div className="form-error-message">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success-message">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                    <polyline points="22 4 12 14.01 9 11.01"/>
                                </svg>
                                Profile saved successfully!
                            </div>
                        )}
                    </div>
                </div>

                <div className="edit-profile-modal-footer">
                    <button 
                        onClick={onClose} 
                        className="btn-cancel" 
                        disabled={saving}
                        type="button"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSave} 
                        className="btn-save" 
                        disabled={saving || !touched || !isFormValid()}
                        type="button"
                    >
                        {saving ? (
                            <>
                                <svg className="spinner-small" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                </svg>
                                Saving...
                            </>
                        ) : (
                            <>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                    <polyline points="17 21 17 13 7 13 7 21"/>
                                    <polyline points="7 3 7 8 15 8"/>
                                </svg>
                                Save Changes
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Profile = ({ initialTab }) => {
    const { userId: urlUserId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState({
        name: '',
        bio: '',
        profilePicture: '',
        socialLinks: { twitter: '', instagram: '', facebook: '' },
    });
    const [tracks, setTracks] = useState([]);
    const [followers, setFollowers] = useState([]);
    const [following, setFollowing] = useState([]);
    const [followersProfiles, setFollowersProfiles] = useState([]);
    const [followingProfiles, setFollowingProfiles] = useState([]);
    const [currentUserFollowing, setCurrentUserFollowing] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [activeTab, setActiveTab] = useState(initialTab || 'tracks');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [user, setUser] = useState(null);
    const [profileUserId, setProfileUserId] = useState(null);
    const [isOwnProfile, setIsOwnProfile] = useState(true);
    const [showEdit, setShowEdit] = useState(false);
    const [profileTheme, setProfileTheme] = useState(() => {
        if (typeof window === 'undefined') return 'amber';
        return normalizeProfileTheme(window.localStorage.getItem('tunecraft-profile-theme'));
    });
    const [earnings, setEarnings] = useState({
        totalEarnings: '0.00',
        totalSales: 0,
        pendingPayout: '0.00',
        salesHistory: [],
        earningsBreakdown: []
    });
    const [earningsLoading, setEarningsLoading] = useState(false);
    const [stripeConnect, setStripeConnect] = useState(null);
    const [payoutPreferences, setPayoutPreferences] = useState({
        mode: 'stripe',
        manualMethod: '',
        payoutHandle: '',
        manualPayoutEnabled: false,
    });
    const [payoutBalances, setPayoutBalances] = useState({
        withdrawableBalance: 0,
        withdrawalPending: 0,
        totalWithdrawn: 0,
    });
    const [withdrawalAmount, setWithdrawalAmount] = useState('');
    const [withdrawalLoading, setWithdrawalLoading] = useState(false);
    const [withdrawalMessage, setWithdrawalMessage] = useState('');
    const [payoutPreferencesSaved, setPayoutPreferencesSaved] = useState(false);
    const [connectLoading, setConnectLoading] = useState(false);
    const [connectError, setConnectError] = useState('');
    const [playingTrackId, setPlayingTrackId] = useState('');

    // Listen for auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUser(firebaseUser);
        });
        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem('tunecraft-profile-theme', profileTheme);
        }
    }, [profileTheme]);

    useEffect(() => {
        const requestedTab = initialTab || searchParams.get('tab');
        if (requestedTab === 'settings' && isOwnProfile) {
            setActiveTab('settings');
        }
    }, [initialTab, isOwnProfile, searchParams]);

    useEffect(() => {
        const fetchCurrentUserFollowing = async () => {
            if (!user) {
                setCurrentUserFollowing([]);
                return;
            }
            try {
                const profileDoc = await getDoc(doc(db, 'profiles', user.uid));
                const followingList = profileDoc.exists() ? profileDoc.data().following || [] : [];
                setCurrentUserFollowing(Array.isArray(followingList) ? followingList : []);
            } catch (err) {
                console.error('Error fetching current user following:', err);
            }
        };
        fetchCurrentUserFollowing();
    }, [user]);

    // Determine which profile to view
    useEffect(() => {
        if (urlUserId) {
            setProfileUserId(urlUserId);
            setIsOwnProfile(user && user.uid === urlUserId);
        } else if (user) {
            setProfileUserId(user.uid);
            setIsOwnProfile(true);
        } else {
            setProfileUserId(null);
            setIsOwnProfile(false);
        }
    }, [urlUserId, user]);

    // Fetch user profiles for followers/following lists
    const fetchUserProfiles = async (userIds, setProfiles) => {
        if (!userIds || userIds.length === 0) {
            setProfiles([]);
            return;
        }
        try {
            const profiles = await Promise.all(
                userIds.map(async (userId) => {
                    try {
                        const profileDoc = await getDoc(doc(db, 'profiles', userId));
                        if (profileDoc.exists()) {
                            const data = profileDoc.data();
                            return {
                                id: userId,
                                name: data.profile?.name || 'Unknown User',
                                bio: data.profile?.bio || '',
                                profilePicture: data.profile?.profilePicture || '',
                            };
                        }
                        return null;
                    } catch (err) {
                        console.error(`Error fetching profile for ${userId}:`, err);
                        return null;
                    }
                })
            );
            setProfiles(profiles.filter(p => p !== null));
        } catch (err) {
            console.error('Error fetching user profiles:', err);
        }
    };

    // Fetch profile data from Firestore
    useEffect(() => {
        if (!profileUserId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const fetchProfile = async () => {
            try {
                const docRef = doc(db, 'profiles', profileUserId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const normalizedProfileDoc = buildUserProfileDocument({
                        user: isOwnProfile ? user : null,
                        existing: data,
                    });

                    setProfile(normalizedProfileDoc.profile);
                    const followersList = normalizedProfileDoc.followers;
                    const followingList = normalizedProfileDoc.following;
                    setFollowers(followersList);
                    setFollowing(followingList);
                    setRecentActivity(normalizedProfileDoc.recentActivity);
                    setStripeConnect(normalizedProfileDoc.stripeConnect || data.stripeConnect || null);
                    setPayoutPreferences(normalizedProfileDoc.payoutPreferences || {
                        mode: 'stripe',
                        manualMethod: '',
                        payoutHandle: '',
                        manualPayoutEnabled: false,
                    });
                    setPayoutBalances({
                        withdrawableBalance: Number(normalizedProfileDoc.withdrawableBalance || data.withdrawableBalance || data.pendingPayout || 0),
                        withdrawalPending: Number(normalizedProfileDoc.withdrawalPending || data.withdrawalPending || 0),
                        totalWithdrawn: Number(normalizedProfileDoc.totalWithdrawn || data.totalWithdrawn || 0),
                    });

                    if (isOwnProfile && user) {
                        await setDoc(docRef, normalizedProfileDoc, { merge: true });
                    }
                    
                    // Fetch actual user profiles for followers and following
                    if (followersList.length > 0) {
                        fetchUserProfiles(followersList, setFollowersProfiles);
                    }
                    if (followingList.length > 0) {
                        fetchUserProfiles(followingList, setFollowingProfiles);
                    }
                } else {
                    const defaultProfileDoc = isOwnProfile && user
                        ? buildUserProfileDocument({ user })
                        : buildUserProfileDocument({ name: 'Unknown User' });

                    if (isOwnProfile && user) {
                        await setDoc(docRef, defaultProfileDoc, { merge: true });
                    }

                    setProfile(defaultProfileDoc.profile);
                    setTracks([]);
                    setFollowers([]);
                    setFollowing([]);
                    setRecentActivity(defaultProfileDoc.recentActivity);
                    setStripeConnect(defaultProfileDoc.stripeConnect || null);
                    setPayoutPreferences(defaultProfileDoc.payoutPreferences || {
                        mode: 'stripe',
                        manualMethod: '',
                        payoutHandle: '',
                        manualPayoutEnabled: false,
                    });
                    setPayoutBalances({
                        withdrawableBalance: Number(defaultProfileDoc.withdrawableBalance || 0),
                        withdrawalPending: Number(defaultProfileDoc.withdrawalPending || 0),
                        totalWithdrawn: Number(defaultProfileDoc.totalWithdrawn || 0),
                    });
                }
                
                // Fetch tracks for this user
                const tracksQuery = query(collection(db, 'tracks'), where('userId', '==', profileUserId));
                const tracksSnapshot = await getDocs(tracksQuery);
                const userTracks = tracksSnapshot.docs
                    .map(normalizeTrackDocument)
                    .filter(hasPlayableAudio);
                setTracks(userTracks);

                const trackArtistName = getTrackArtistName(userTracks);
                if (trackArtistName) {
                    setProfile((currentProfile) => {
                        const currentName = currentProfile?.name || '';
                        const shouldUseTrackArtist = !currentName || currentName === 'Unknown User' || currentName === 'Tune Producer';

                        if (!shouldUseTrackArtist) return currentProfile;

                        return {
                            ...currentProfile,
                            name: trackArtistName,
                            bio: currentProfile?.bio || `${userTracks.length} beat${userTracks.length === 1 ? '' : 's'} live on Tune.`,
                        };
                    });
                }
                
                setError('');
            } catch (err) {
                console.error('Error fetching profile:', err);
                setError('Failed to load profile.');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
        if (isOwnProfile && user) {
            fetchEarnings();
        }
        // fetchEarnings is a local async helper; this effect is keyed to the loaded profile/user.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileUserId, isOwnProfile, user]);

    useEffect(() => {
        const connectReturn = searchParams.get('connect');
        if (!isOwnProfile || !user || activeTab !== 'settings' || connectReturn !== 'return') return;

        const refreshStatus = async () => {
            setConnectLoading(true);
            setConnectError('');
            try {
                const status = await refreshConnectAccountStatus();
                setStripeConnect(status);
            } catch (err) {
                setConnectError(err.message || 'Could not refresh Stripe payout status.');
            } finally {
                setConnectLoading(false);
            }
        };

        refreshStatus();
    }, [activeTab, isOwnProfile, searchParams, user]);

    async function fetchEarnings() {
        if (!user || !isOwnProfile) return;
        setEarningsLoading(true);
        try {
            const purchasesQuery = query(
                collection(db, 'purchases'),
                where('sellerId', '==', user.uid),
                orderBy('purchaseDate', 'desc')
            );
            const purchasesSnapshot = await getDocs(purchasesQuery);
            
            let totalEarnings = 0;
            let totalSales = 0;
            let pendingPayout = 0;
            const salesHistory = [];
            const trackEarningsMap = new Map();

            purchasesSnapshot.docs.forEach(doc => {
                const data = doc.data();
                const artistRevenue = Number(data.artistEarnings ?? data.artistRevenue ?? 0);
                totalEarnings += artistRevenue;
                totalSales++;
                pendingPayout += artistRevenue;

                salesHistory.push({
                    id: doc.id,
                    ...data,
                    purchaseDate: data.purchaseDate?.toDate ? data.purchaseDate.toDate().toLocaleDateString() : new Date().toLocaleDateString(),
                    artistRevenue: artistRevenue.toFixed(2),
                    price: data.price ? data.price.toFixed(2) : '0.00',
                });

                if (data.trackId) {
                    const currentTrackEarnings = trackEarningsMap.get(data.trackId) || {
                        title: data.trackTitle || 'Unknown Track',
                        genre: data.trackGenre || 'Uncategorized',
                        price: data.price || 0,
                        salesCount: 0,
                        totalTrackEarnings: 0,
                    };
                    currentTrackEarnings.salesCount++;
                    currentTrackEarnings.totalTrackEarnings += artistRevenue;
                    trackEarningsMap.set(data.trackId, currentTrackEarnings);
                }
            });

            const earningsBreakdown = Array.from(trackEarningsMap.values()).map(item => ({
                ...item,
                totalTrackEarnings: item.totalTrackEarnings.toFixed(2),
                price: item.price.toFixed(2),
            }));

            setEarnings({
                totalEarnings: totalEarnings.toFixed(2),
                totalSales,
                pendingPayout: pendingPayout.toFixed(2),
                salesHistory,
                earningsBreakdown,
            });

        } catch (err) {
            console.error('Error fetching earnings:', err);
        } finally {
            setEarningsLoading(false);
        }
    }

    // Remove unused functions
    // const handleProfileUpdate = (field, value) => {
    //     setProfile({ ...profile, [field]: value });
    // };

    // const handleSocialLinkUpdate = (platform, value) => {
    //     setProfile({
    //         ...profile,
    //         socialLinks: { ...profile.socialLinks, [platform]: value },
    //     });
    // };

    const handleDelete = async (id) => {
        const trackToDelete = tracks.find(track => track.id === id);
        const trackTitle = trackToDelete?.title || 'this track';
        if (!window.confirm(`Delete "${trackTitle}" from your storefront?`)) return;

        try {
            await deleteDoc(doc(db, 'tracks', id));
            setTracks(currentTracks => currentTracks.filter(track => track.id !== id));
            setError('');
        } catch (err) {
            console.error('Error deleting track:', err);
            setError('Could not delete this track. Please try again.');
        }
    };

    const handleEdit = async (id) => {
        const trackToEdit = tracks.find(track => track.id === id);
        if (!trackToEdit) return;
        const newTitle = prompt('Enter new title:', trackToEdit.title);
        const newPrice = prompt('Enter new price:', trackToEdit.price);
        if (!newTitle || !newPrice) return;

        const numericPrice = Number(newPrice);
        if (!Number.isFinite(numericPrice) || numericPrice < 0) {
            setError('Please enter a valid track price.');
            return;
        }

        try {
            await updateDoc(doc(db, 'tracks', id), {
                title: newTitle.trim(),
                price: numericPrice,
            });
            setTracks(currentTracks => currentTracks.map(track =>
                track.id === id ? { ...track, title: newTitle.trim(), price: numericPrice } : track
            ));
            setError('');
        } catch (err) {
            console.error('Error editing track:', err);
            setError('Could not update this track. Please try again.');
        }
    };

    const handleTrackPreview = (trackId) => {
        const selectedAudio = document.getElementById(`profile-track-audio-${trackId}`);
        if (!selectedAudio) return;

        document.querySelectorAll('.track-audio-preview').forEach((audio) => {
            if (audio !== selectedAudio) {
                audio.pause();
                audio.currentTime = 0;
            }
        });

        if (playingTrackId === trackId) {
            selectedAudio.pause();
            setPlayingTrackId('');
            return;
        }

        selectedAudio.play()
            .then(() => setPlayingTrackId(trackId))
            .catch(() => setError('Could not play this preview.'));
    };

    // Logout handler
    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/');
        } catch (err) {
            setError('Failed to log out.');
        }
    };

    // Add this function to handle saving from the modal and persist to Firestore
    const handleEditProfile = async (newProfile) => {
        setShowEdit(false);
        if (!user) return;
        try {
            const nextActivity = [`Edited profile on ${new Date().toLocaleDateString()}`, ...recentActivity];
            const nextDisplayName = newProfile.name.trim();
            const previousDisplayName = profile.name || user.displayName || '';
            const nextUsername = makeUsername(newProfile.username || nextDisplayName, user.email);
            const normalizedProfile = {
                ...newProfile,
                name: nextDisplayName,
                username: nextUsername,
            };
            setProfile(normalizedProfile);
            if (nextDisplayName && user.displayName !== nextDisplayName) {
                await updateProfile(user, { displayName: nextDisplayName });
            }
            const profileDocument = buildUserProfileDocument({
                user,
                name: nextDisplayName,
                username: nextUsername,
                existing: {
                    profile: {
                        ...normalizedProfile,
                    },
                    tracks,
                    followers,
                    following,
                    recentActivity: nextActivity,
                    stripeConnect,
                    payoutPreferences,
                },
            });
            const batch = writeBatch(db);
            batch.set(doc(db, 'profiles', user.uid), profileDocument, { merge: true });

            if (nextDisplayName && nextDisplayName !== previousDisplayName) {
                const tracksSnapshot = await getDocs(query(collection(db, 'tracks'), where('userId', '==', user.uid)));
                tracksSnapshot.docs.slice(0, 499).forEach((trackDoc) => {
                    batch.set(trackDoc.ref, {
                        artist: nextDisplayName,
                        updatedAt: new Date(),
                    }, { merge: true });
                });
            }

            await batch.commit();
            if (nextDisplayName && nextDisplayName !== previousDisplayName) {
                setTracks((currentTracks) => currentTracks.map((track) => ({
                    ...track,
                    artist: nextDisplayName,
                })));
            }
            setRecentActivity(nextActivity);
            setError('');
        } catch (err) {
            setError('Failed to save profile.');
        }
    };

    const handleStartConnectOnboarding = async () => {
        setConnectLoading(true);
        setConnectError('');
        try {
            const result = await createConnectOnboardingLink();
            if (result.status) {
                setStripeConnect(result.status);
            }
            window.location.assign(result.url);
        } catch (err) {
            setConnectError(err.message || 'Could not start Stripe payout setup.');
            setConnectLoading(false);
        }
    };

    const handleRefreshConnectStatus = async () => {
        setConnectLoading(true);
        setConnectError('');
        try {
            const status = await refreshConnectAccountStatus();
            setStripeConnect(status);
        } catch (err) {
            setConnectError(err.message || 'Could not refresh Stripe payout status.');
        } finally {
            setConnectLoading(false);
        }
    };

    const handleSavePayoutPreferences = async () => {
        if (!user) return;

        const nextPreferences = {
            mode: payoutPreferences.mode === 'manual' ? 'manual' : 'stripe',
            manualMethod: payoutPreferences.manualMethod || '',
            payoutHandle: payoutPreferences.payoutHandle.trim(),
            manualPayoutEnabled: payoutPreferences.mode === 'manual' && Boolean(payoutPreferences.manualMethod) && payoutPreferences.payoutHandle.trim().length >= 3,
            updatedAt: new Date().toISOString(),
        };

        try {
            await setDoc(
                doc(db, 'profiles', user.uid),
                {
                    payoutPreferences: nextPreferences,
                    updatedAt: new Date(),
                },
                { merge: true }
            );
            setPayoutPreferences(nextPreferences);
            setPayoutPreferencesSaved(true);
            setTimeout(() => setPayoutPreferencesSaved(false), 2400);
            setConnectError('');
        } catch (err) {
            setConnectError(err.message || 'Could not save payout settings.');
        }
    };

    const handleRequestWithdrawal = async () => {
        const amount = Number(withdrawalAmount || payoutBalances.withdrawableBalance || 0);
        if (!Number.isFinite(amount) || amount < 1) {
            setWithdrawalMessage('Enter at least $1.00 to request a withdrawal.');
            return;
        }

        setWithdrawalLoading(true);
        setWithdrawalMessage('');
        try {
            const result = await requestWithdrawal({ amount });
            const requestedAmount = Number(result.amount || amount);
            setPayoutBalances((current) => ({
                ...current,
                withdrawableBalance: Math.max(0, Number((current.withdrawableBalance - requestedAmount).toFixed(2))),
                withdrawalPending: Number((current.withdrawalPending + requestedAmount).toFixed(2)),
            }));
            setWithdrawalAmount('');
            setWithdrawalMessage(`Withdrawal request submitted for $${formatMoney(requestedAmount)}.`);
        } catch (err) {
            setWithdrawalMessage(err.message || 'Could not request withdrawal.');
        } finally {
            setWithdrawalLoading(false);
        }
    };

    if (loading) return <div className="profile-loading">Loading profile...</div>;
    if (!profileUserId) {
        if (!user) return <AuthForm />;
        if (user && !urlUserId) {
            const queryString = searchParams.toString();
            navigate(`/profile/${user.uid}${queryString ? `?${queryString}` : ''}`, { replace: true });
            return <div className="profile-loading">Loading profile...</div>;
        }
    }

    const inferredArtistName = getTrackArtistName(tracks);
    const displayName = profile.name && profile.name !== 'Unknown User'
        ? profile.name
        : inferredArtistName || 'Tune Producer';
    const displayBio = profile.bio || (tracks.length
        ? `${tracks.length} beat${tracks.length === 1 ? '' : 's'} live. Preview the catalog and license directly from the marketplace.`
        : 'Producer storefront is getting set up.');
    const profileInitial = displayName.trim().charAt(0).toUpperCase() || 'T';
    const connectCapabilityStatus = stripeConnect?.capabilityStatus || 'not_started';
    const connectReady = stripeConnect?.onboardingComplete || connectCapabilityStatus === 'active';
    const connectStatusCopy = connectReady
        ? 'Stripe Connect is ready as a withdrawal method for your Tune balance.'
        : stripeConnect?.accountId
            ? 'Finish Stripe onboarding or refresh after Stripe verifies your details.'
            : 'Set up Stripe Connect if you want Stripe as your withdrawal method.';
    const manualPayoutReady = payoutPreferences.manualPayoutEnabled === true;
    const payoutReady = connectReady || manualPayoutReady;
    const payoutStatusLabel = connectReady
        ? 'Automatic payouts ready'
        : manualPayoutReady
            ? 'Manual payouts ready'
            : 'Withdrawal method not set';
    const payoutStatusCopy = connectReady
        ? 'Sales are added to your Tune balance, and Stripe Connect can be used when you withdraw.'
        : manualPayoutReady
            ? 'Sales are added to your Tune balance and can be withdrawn using your saved manual method.'
            : 'Buyers can still license your beats. Add a withdrawal method so you can cash out your Tune balance.';
    const connectChecklist = [
        {
            label: 'Create Stripe Express account',
            complete: Boolean(stripeConnect?.accountId),
        },
        {
            label: 'Submit identity and banking details',
            complete: connectReady || (Boolean(stripeConnect?.accountId) && connectCapabilityStatus !== 'not_started'),
        },
        {
            label: 'Stripe transfers active',
            complete: connectReady,
        },
    ];
    const manualPayoutChecklist = [
        {
            label: 'Choose withdrawal method',
            complete: Boolean(payoutPreferences.manualMethod),
        },
        {
            label: 'Add payout email or handle',
            complete: payoutPreferences.payoutHandle.trim().length >= 3,
        },
        {
            label: 'Manual withdrawal saved',
            complete: manualPayoutReady,
        },
    ];
    const payoutChecklist = payoutPreferences.mode === 'manual' ? manualPayoutChecklist : connectChecklist;

    return (
        <div className={`profile-page-modern profile-theme-${profileTheme}`}>
            {showEdit && (
                <EditProfileModal
                    profile={profile}
                    onSave={handleEditProfile}
                    onClose={() => setShowEdit(false)}
                    user={user}
                />
            )}
            {error && <div className="profile-error-message">{error}</div>}
            
            {/* Profile Banner */}
            <div className="profile-banner">
                <div className="banner-overlay"></div>
                <div className="banner-content">
                    <div className="profile-avatar-large">
                        {profile.profilePicture ? (
                            <img
                                src={profile.profilePicture}
                                alt={displayName}
                                className="avatar-image"
                            />
                        ) : (
                            <div className="profile-avatar-initials" aria-label={displayName}>
                                {profileInitial}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Profile Header */}
            <div className="profile-header-modern">
                <div className="container-profile">
                    <div className="profile-header-content">
                        <div className="profile-main-info">
                            <span className="profile-kicker">Producer storefront</span>
                            <h1 className="profile-name">{displayName}</h1>
                            <p className="profile-bio-text">{displayBio}</p>
                            
                            {/* Social Links */}
                            {(profile.socialLinks?.twitter || profile.socialLinks?.instagram || profile.socialLinks?.facebook) && (
                                <div className="social-links-modern">
                                    {profile.socialLinks.twitter && (
                                        <a href={profile.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="social-link">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                                            </svg>
                                        </a>
                                    )}
                                    {profile.socialLinks.instagram && (
                                        <a href={profile.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="social-link">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                                            </svg>
                                        </a>
                                    )}
                                    {profile.socialLinks.facebook && (
                                        <a href={profile.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="social-link">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                                            </svg>
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="profile-actions-modern">
                            {isOwnProfile ? (
                                <>
                                    <button className="btn-primary" onClick={() => setShowEdit(true)}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                        </svg>
                                        Edit Profile
                                    </button>
                                    <button className="btn-secondary" onClick={handleLogout}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                            <polyline points="16 17 21 12 16 7"/>
                                            <line x1="21" y1="12" x2="9" y2="12"/>
                                        </svg>
                                        Log Out
                                    </button>
                                </>
                            ) : (
                                user && profileUserId && (
                                    <FollowButton
                                        targetUserId={profileUserId}
                                        targetUserName={displayName}
                                        currentUserFollowing={currentUserFollowing}
                                        onFollowChange={(targetUserId, isFollowing) => {
                                            setCurrentUserFollowing((prev) => isFollowing
                                                ? [...new Set([...prev, targetUserId])]
                                                : prev.filter((id) => id !== targetUserId));
                                            setFollowers((prev) => isFollowing
                                                ? [...new Set([...prev, user.uid])]
                                                : prev.filter((id) => id !== user.uid));
                                        }}
                                    />
                                )
                            )}
                            <button className="btn-secondary" onClick={() => navigate(`/marketplace?artist=${encodeURIComponent(displayName || '')}`)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"/>
                                    <path d="m21 21-4.35-4.35"/>
                                </svg>
                                View Store
                            </button>
                        </div>
                    </div>

                    {/* Stats Section */}
                    <div className="profile-stats-modern">
                        <div className="stat-card">
                            <div className="stat-value">{tracks.length}</div>
                            <div className="stat-label">Tracks</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">{followers.length}</div>
                            <div className="stat-label">Followers</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">{following.length}</div>
                            <div className="stat-label">Following</div>
                        </div>
                        {isOwnProfile && earnings.totalSales > 0 && (
                            <>
                                <div className="stat-card stat-card-highlight">
                                    <div className="stat-value">${earnings.totalEarnings}</div>
                                    <div className="stat-label">Total Earnings</div>
                                </div>
                                <div className="stat-card stat-card-highlight">
                                    <div className="stat-value">{earnings.totalSales}</div>
                                    <div className="stat-label">Total Sales</div>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="profile-storefront-strip" aria-label="Producer storefront promises">
                        <span>Verified producer page</span>
                        <span>{tracks.length} beat{tracks.length === 1 ? '' : 's'} live</span>
                        <span>$0 buyer fee marketplace</span>
                        <span>Multi-genre discovery</span>
                    </div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className="profile-tabs-modern">
                <div className="container-profile">
                    <div className="tabs-wrapper">
                        <button
                            className={`tab-button ${activeTab === 'tracks' ? 'active' : ''}`}
                            onClick={() => setActiveTab('tracks')}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18V5l12-2v13"/>
                                <circle cx="6" cy="18" r="3"/>
                                <circle cx="18" cy="16" r="3"/>
                            </svg>
                            Tracks ({tracks.length})
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'followers' ? 'active' : ''}`}
                            onClick={() => setActiveTab('followers')}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                            Followers ({followers.length})
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'following' ? 'active' : ''}`}
                            onClick={() => setActiveTab('following')}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="8.5" cy="7" r="4"/>
                                <line x1="20" y1="8" x2="20" y2="14"/>
                                <line x1="23" y1="11" x2="17" y2="11"/>
                            </svg>
                            Following ({following.length})
                        </button>
                        {isOwnProfile && (
                            <button
                                className={`tab-button ${activeTab === 'earnings' ? 'active' : ''}`}
                                onClick={() => setActiveTab('earnings')}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="1" x2="12" y2="23"/>
                                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                </svg>
                                Earnings
                            </button>
                        )}
                        {isOwnProfile && (
                            <button
                                className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
                                onClick={() => setActiveTab('analytics')}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 3v18h18"/>
                                    <path d="M7 14l4-4 3 3 5-6"/>
                                </svg>
                                Analytics
                            </button>
                        )}
                        <button
                            className={`tab-button ${activeTab === 'activity' ? 'active' : ''}`}
                            onClick={() => setActiveTab('activity')}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                            </svg>
                            Activity
                        </button>
                        {isOwnProfile && (
                            <button
                                className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
                                onClick={() => setActiveTab('settings')}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="3"/>
                                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4a1.65 1.65 0 0 0 1-1.51V2a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09A1.65 1.65 0 0 0 19.4 15z"/>
                                </svg>
                                Settings
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            <div className="profile-content-modern">
                <div className="container-profile">
                    {activeTab === 'tracks' && (
                        <div className="tracks-section-modern">
                            {isOwnProfile && (
                                <div className="section-header-modern">
                                    <h2 className="section-title-modern">My Tracks</h2>
                                    <button className="btn-primary" onClick={() => navigate('/upload')}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="12" y1="5" x2="12" y2="19"/>
                                            <line x1="5" y1="12" x2="19" y2="12"/>
                                        </svg>
                                        Upload New Track
                                    </button>
                                </div>
                            )}
                            {tracks.length === 0 ? (
                                <div className="empty-state-modern">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M9 18V5l12-2v13"/>
                                        <circle cx="6" cy="18" r="3"/>
                                        <circle cx="18" cy="16" r="3"/>
                                    </svg>
                                    <h3>No tracks yet</h3>
                                    <p>{isOwnProfile ? 'Upload your first track to get started!' : 'This artist hasn\'t uploaded any tracks yet.'}</p>
                                    {isOwnProfile && (
                                        <button className="btn-primary" onClick={() => navigate('/upload')}>
                                            Upload Track
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="tracks-grid-modern">
                                    {tracks.map(track => (
                                        <div className="track-card-modern" key={track.id}>
                                            <div className="track-card-artwork">
                                                {track.coverImage ? (
                                                    <img src={track.coverImage} alt={track.title} />
                                                ) : (
                                                    <div className="track-artwork-placeholder"></div>
                                                )}
                                                {track.fileUrl && (
                                                    <div className="track-play-overlay">
                                                        <button
                                                            className="track-play-btn"
                                                            type="button"
                                                            onClick={() => handleTrackPreview(track.id)}
                                                            aria-label={`${playingTrackId === track.id ? 'Pause' : 'Preview'} ${track.title || 'track'}`}
                                                        >
                                                            {playingTrackId === track.id ? (
                                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                                    <path d="M6 5h4v14H6zM14 5h4v14h-4z"/>
                                                                </svg>
                                                            ) : (
                                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                                    <path d="M8 5v14l11-7z"/>
                                                                </svg>
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                                {track.fileUrl && (
                                                    <audio
                                                        id={`profile-track-audio-${track.id}`}
                                                        className="track-audio-preview"
                                                        src={track.fileUrl}
                                                        preload="metadata"
                                                        onEnded={() => setPlayingTrackId('')}
                                                        onPause={() => {
                                                            if (playingTrackId === track.id) {
                                                                setPlayingTrackId('');
                                                            }
                                                        }}
                                                    ></audio>
                                                )}
                                            </div>
                                            <div className="track-card-info">
                                                <h3 className="track-card-title">{track.title || 'Untitled'}</h3>
                                                <div className="track-card-meta">
                                                    <span className="track-genre-badge">{getTrackGenres(track).join(' / ')}</span>
                                                    <span className="track-price-badge">${formatTrackPrice(track.price)}</span>
                                                </div>
                                                {isOwnProfile && (
                                                    <div className="track-card-actions">
                                                        <button 
                                                            className="action-btn edit-action" 
                                                            onClick={() => handleEdit(track.id)}
                                                            title="Edit track"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                            </svg>
                                                        </button>
                                                        <button 
                                                            className="action-btn delete-action" 
                                                            onClick={() => handleDelete(track.id)}
                                                            title="Delete track"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <polyline points="3 6 5 6 21 6"/>
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'followers' && (
                        <div className="followers-section">
                            {followers.length === 0 ? (
                                <div className="empty-state-modern">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                        <circle cx="9" cy="7" r="4"/>
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                    </svg>
                                    <h3>No followers yet</h3>
                                    <p>Share your profile to get followers</p>
                                </div>
                            ) : (
                                <div className="users-list">
                                    {followersProfiles.length > 0 ? (
                                        followersProfiles.map((followerProfile) => (
                                            <Link key={followerProfile.id} to={`/profile/${followerProfile.id}`} className="user-item">
                                                <div className="user-avatar-small">
                                                    {followerProfile.profilePicture ? (
                                                        <img src={followerProfile.profilePicture} alt={followerProfile.name} />
                                                    ) : (
                                                        followerProfile.name.charAt(0).toUpperCase()
                                                    )}
                                                </div>
                                                <div className="user-info">
                                                    <span className="user-name">{followerProfile.name}</span>
                                                    {followerProfile.bio && (
                                                        <span className="user-bio">{followerProfile.bio}</span>
                                                    )}
                                                </div>
                                                {user && followerProfile.id !== user.uid && (
                                                    <FollowButton
                                                        targetUserId={followerProfile.id}
                                                        targetUserName={followerProfile.name}
                                                        currentUserFollowing={currentUserFollowing}
                                                        onFollowChange={(targetUserId, isFollowing) => {
                                                            setCurrentUserFollowing((prev) => isFollowing
                                                                ? [...new Set([...prev, targetUserId])]
                                                                : prev.filter((id) => id !== targetUserId));
                                                        }}
                                                    />
                                                )}
                                            </Link>
                                        ))
                                    ) : (
                                        followers.map((followerId, index) => (
                                            <Link key={index} to={`/profile/${followerId}`} className="user-item">
                                                <div className="user-avatar-small">
                                                    {typeof followerId === 'string' ? followerId.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                                <span className="user-name">{followerId}</span>
                                            </Link>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'following' && (
                        <div className="following-section">
                            {following.length === 0 ? (
                                <div className="empty-state-modern">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                        <circle cx="8.5" cy="7" r="4"/>
                                        <line x1="20" y1="8" x2="20" y2="14"/>
                                        <line x1="23" y1="11" x2="17" y2="11"/>
                                    </svg>
                                    <h3>Not following anyone yet</h3>
                                    <p>Discover artists and follow them</p>
                                </div>
                            ) : (
                                <div className="users-list">
                                    {followingProfiles.length > 0 ? (
                                        followingProfiles.map((followingProfile) => (
                                            <Link key={followingProfile.id} to={`/profile/${followingProfile.id}`} className="user-item">
                                                <div className="user-avatar-small">
                                                    {followingProfile.profilePicture ? (
                                                        <img src={followingProfile.profilePicture} alt={followingProfile.name} />
                                                    ) : (
                                                        followingProfile.name.charAt(0).toUpperCase()
                                                    )}
                                                </div>
                                                <div className="user-info">
                                                    <span className="user-name">{followingProfile.name}</span>
                                                    {followingProfile.bio && (
                                                        <span className="user-bio">{followingProfile.bio}</span>
                                                    )}
                                                </div>
                                                {user && followingProfile.id !== user.uid && (
                                                    <FollowButton
                                                        targetUserId={followingProfile.id}
                                                        targetUserName={followingProfile.name}
                                                        currentUserFollowing={currentUserFollowing}
                                                        onFollowChange={(targetUserId, isFollowing) => {
                                                            setCurrentUserFollowing((prev) => isFollowing
                                                                ? [...new Set([...prev, targetUserId])]
                                                                : prev.filter((id) => id !== targetUserId));
                                                        }}
                                                    />
                                                )}
                                            </Link>
                                        ))
                                    ) : (
                                        following.map((followedId, index) => (
                                            <Link key={index} to={`/profile/${followedId}`} className="user-item">
                                                <div className="user-avatar-small">
                                                    {typeof followedId === 'string' ? followedId.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                                <span className="user-name">{followedId}</span>
                                            </Link>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'earnings' && isOwnProfile && (
                        <div className="earnings-section-modern">
                            {earningsLoading ? (
                                <div className="loading-state">
                                    <div className="spinner"></div>
                                    <p>Loading earnings...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="earnings-overview">
                                        <div className="earnings-card earnings-card-primary">
                                            <div className="earnings-icon">
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="12" y1="1" x2="12" y2="23"/>
                                                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                                </svg>
                                            </div>
                                            <div className="earnings-content">
                                                <div className="earnings-label">Total Earnings</div>
                                                <div className="earnings-amount">${earnings.totalEarnings}</div>
                                            </div>
                                        </div>
                                        <div className="earnings-card">
                                            <div className="earnings-icon">
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                    <circle cx="8.5" cy="7" r="4"/>
                                                    <line x1="20" y1="8" x2="20" y2="14"/>
                                                    <line x1="23" y1="11" x2="17" y2="11"/>
                                                </svg>
                                            </div>
                                            <div className="earnings-content">
                                                <div className="earnings-label">Total Sales</div>
                                                <div className="earnings-amount">{earnings.totalSales}</div>
                                            </div>
                                        </div>
                                        <div className="earnings-card">
                                            <div className="earnings-icon">
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"/>
                                                    <polyline points="12 6 12 12 16 14"/>
                                                </svg>
                                            </div>
                                            <div className="earnings-content">
                                                <div className="earnings-label">Pending Payout</div>
                                                <div className="earnings-amount">${earnings.pendingPayout}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {earnings.earningsBreakdown.length > 0 && (
                                        <div className="earnings-breakdown-section">
                                            <h3 className="section-title-modern">Earnings by Track</h3>
                                            <div className="earnings-breakdown">
                                                {earnings.earningsBreakdown.map((track, index) => (
                                                    <div key={index} className="breakdown-item">
                                                        <div className="breakdown-track-info">
                                                            <div className="breakdown-track-title">{track.title}</div>
                                                            <div className="breakdown-track-meta">
                                                                <span className="breakdown-genre">{track.genre}</span>
                                                                <span className="breakdown-sales">{track.salesCount} sales</span>
                                                            </div>
                                                        </div>
                                                        <div className="breakdown-earnings">
                                                            <div className="breakdown-amount">${track.totalTrackEarnings}</div>
                                                            <div className="breakdown-price">${track.price} per sale</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {earnings.salesHistory.length > 0 && (
                                        <div className="sales-history-section">
                                            <h3 className="section-title-modern">Sales History</h3>
                                            <div className="sales-history">
                                                {earnings.salesHistory.map((sale) => (
                                                    <div key={sale.id} className="sale-item">
                                                        <div className="sale-info">
                                                            <div className="sale-track-title">{sale.trackTitle || 'Unknown Track'}</div>
                                                            <div className="sale-date">{sale.purchaseDate}</div>
                                                        </div>
                                                        <div className="sale-amount">${sale.artistRevenue}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {earnings.totalSales === 0 && (
                                        <div className="empty-state-modern">
                                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <line x1="12" y1="1" x2="12" y2="23"/>
                                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                            </svg>
                                            <h3>No earnings yet</h3>
                                            <p>Start selling your tracks to see earnings here!</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'analytics' && isOwnProfile && (
                        <div className="analytics-section-modern">
                            <div className="analytics-overview-modern">
                                <div className="analytics-card-modern">
                                    <span>Total Earnings</span>
                                    <strong>${earnings.totalEarnings}</strong>
                                </div>
                                <div className="analytics-card-modern">
                                    <span>Total Sales</span>
                                    <strong>{earnings.totalSales}</strong>
                                </div>
                                <div className="analytics-card-modern">
                                    <span>Top Track</span>
                                    <strong>{tracks[0]?.title || '—'}</strong>
                                </div>
                                <div className="analytics-card-modern">
                                    <span>Monthly Views</span>
                                    <strong>{tracks.reduce((sum, t) => sum + (t.viewCount || t.views || 0), 0)}</strong>
                                </div>
                            </div>
                            <div className="analytics-panels-modern">
                                <div className="analytics-panel-modern">
                                    <h3>Basic Analytics</h3>
                                    <ul>
                                        <li>Sales count and revenue summary</li>
                                        <li>Top tracks by plays</li>
                                        <li>Recent purchases list</li>
                                        <li>Daily views trend</li>
                                    </ul>
                                </div>
                                <div className="analytics-panel-modern">
                                    <h3>Advanced Analytics</h3>
                                    <ul>
                                        <li>Conversion funnel (views → plays → buys)</li>
                                        <li>Geo + audience breakdown</li>
                                        <li>Revenue by license type</li>
                                        <li>Retention & repeat buyers</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'settings' && isOwnProfile && (
                        <div className="profile-settings-section">
                            <div className="section-header-modern">
                                <h2 className="section-title-modern">Profile Settings</h2>
                            </div>
                            <div className="profile-settings-grid">
                                <section className="profile-settings-card profile-settings-card-wide">
                                    <div className="profile-settings-card-header">
                                        <div>
                                            <span className="profile-settings-eyebrow">Appearance</span>
                                            <h3>Storefront color</h3>
                                        </div>
                                        <span className="profile-settings-current">{profileThemeOptions.find((theme) => theme.id === profileTheme)?.label || 'Amber'}</span>
                                    </div>
                                    <div className="profile-theme-picker profile-theme-picker-settings" aria-label="Profile theme">
                                        <span className="profile-theme-picker-label">Default look</span>
                                        <div className="profile-theme-options">
                                            {profileThemeOptions.map((theme) => (
                                                <button
                                                    key={theme.id}
                                                    type="button"
                                                    className={`profile-theme-option profile-theme-option-${theme.id} ${profileTheme === theme.id ? 'active' : ''}`}
                                                    onClick={() => setProfileTheme(theme.id)}
                                                    aria-pressed={profileTheme === theme.id}
                                                    title={`${theme.label} theme`}
                                                >
                                                    <span className="profile-theme-swatch" aria-hidden="true"></span>
                                                    <span>{theme.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </section>
                                <section className="profile-settings-card profile-settings-card-wide profile-connect-card">
                                    <div className="profile-settings-card-header">
                                        <div>
                                            <span className="profile-settings-eyebrow">Payouts</span>
                                            <h3>Withdraw your Tune balance</h3>
                                        </div>
                                        <span className={`profile-connect-status ${payoutReady ? 'ready' : 'pending'}`}>
                                            {payoutStatusLabel}
                                        </span>
                                    </div>
                                    <p>{payoutStatusCopy}</p>
                                    <div className="profile-connect-layout">
                                        <div className="profile-connect-main">
                                            <div className="profile-payout-mode">
                                                <button
                                                    type="button"
                                                    className={payoutPreferences.mode !== 'manual' ? 'active' : ''}
                                                    onClick={() => setPayoutPreferences((current) => ({ ...current, mode: 'stripe' }))}
                                                >
                                                    Automatic Stripe
                                                </button>
                                                <button
                                                    type="button"
                                                    className={payoutPreferences.mode === 'manual' ? 'active' : ''}
                                                    onClick={() => setPayoutPreferences((current) => ({ ...current, mode: 'manual' }))}
                                                >
                                                    Manual payout
                                                </button>
                                            </div>
                                            {payoutPreferences.mode === 'manual' ? (
                                                <div className="profile-manual-payout-form">
                                                    <label>
                                                        Payout method
                                                        <select
                                                            value={payoutPreferences.manualMethod}
                                                            onChange={(event) => setPayoutPreferences((current) => ({
                                                                ...current,
                                                                manualMethod: event.target.value,
                                                                manualPayoutEnabled: false,
                                                            }))}
                                                        >
                                                            <option value="">Choose method</option>
                                                            <option value="paypal">PayPal</option>
                                                            <option value="cash_app">Cash App</option>
                                                            <option value="venmo">Venmo</option>
                                                            <option value="other">Other</option>
                                                        </select>
                                                    </label>
                                                    <label>
                                                        Payout email or handle
                                                        <input
                                                            type="text"
                                                            value={payoutPreferences.payoutHandle}
                                                            onChange={(event) => setPayoutPreferences((current) => ({
                                                                ...current,
                                                                payoutHandle: event.target.value,
                                                                manualPayoutEnabled: false,
                                                            }))}
                                                            placeholder="PayPal email, $cashtag, @venmo..."
                                                        />
                                                    </label>
                                                    <div className="profile-connect-actions">
                                                        <button className="btn-primary" onClick={handleSavePayoutPreferences}>
                                                            Save manual payout
                                                        </button>
                                                        {payoutPreferencesSaved && <span className="profile-payout-saved">Saved</span>}
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <p>{connectStatusCopy}</p>
                                                    <div className="profile-connect-actions">
                                                        {!connectReady && (
                                                            <button className="btn-primary" onClick={handleStartConnectOnboarding} disabled={connectLoading}>
                                                                {connectLoading ? 'Opening Stripe...' : stripeConnect?.accountId ? 'Continue payout setup' : 'Set up payouts'}
                                                            </button>
                                                        )}
                                                        <button className="btn-secondary" onClick={handleRefreshConnectStatus} disabled={connectLoading}>
                                                            {connectLoading ? 'Checking...' : 'Refresh status'}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                            <div className="profile-connect-details">
                                                <span>Method: {payoutPreferences.mode === 'manual' ? 'Manual payout' : 'Stripe Connect'}</span>
                                                <span>Transfers: {connectCapabilityStatus}</span>
                                                {typeof stripeConnect?.requirementsDueCount === 'number' && (
                                                    <span>Open items: {stripeConnect.requirementsDueCount}</span>
                                                )}
                                                {payoutPreferences.mode === 'manual' && payoutPreferences.manualMethod && (
                                                    <span>Manual: {payoutPreferences.manualMethod.replace('_', ' ')}</span>
                                                )}
                                            </div>
                                            {connectError && <div className="profile-connect-error">{connectError}</div>}
                                        </div>
                                        <div className="profile-connect-checklist" aria-label="Payout setup checklist">
                                            {payoutChecklist.map((item) => (
                                                <div key={item.label} className={`profile-connect-check ${item.complete ? 'complete' : ''}`}>
                                                    <span aria-hidden="true">{item.complete ? 'OK' : '-'}</span>
                                                    <strong>{item.label}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="profile-connect-note">
                                        Buyer payments are processed securely by Stripe and added to your Tune balance. Pick a withdrawal method when you are ready to cash out.
                                    </div>
                                </section>
                                <section className="profile-settings-card">
                                    <div className="profile-settings-card-header">
                                        <div>
                                            <span className="profile-settings-eyebrow">Account</span>
                                            <h3>Profile details</h3>
                                        </div>
                                    </div>
                                    <p>Update your display name, public username, bio, profile picture, and social links.</p>
                                    <div className="profile-settings-summary">
                                        <span>Display name: <strong>{displayName}</strong></span>
                                        {profile.username && <span>Username: <strong>{profile.username}</strong></span>}
                                    </div>
                                    <button className="btn-primary" onClick={() => setShowEdit(true)}>Edit Profile</button>
                                </section>
                                <section className="profile-settings-card">
                                    <div className="profile-settings-card-header">
                                        <div>
                                            <span className="profile-settings-eyebrow">Balance</span>
                                            <h3>Request withdrawal</h3>
                                        </div>
                                    </div>
                                    <div className="profile-withdrawal-balances">
                                        <div>
                                            <span>Available</span>
                                            <strong>${formatMoney(payoutBalances.withdrawableBalance)}</strong>
                                        </div>
                                        <div>
                                            <span>Pending</span>
                                            <strong>${formatMoney(payoutBalances.withdrawalPending)}</strong>
                                        </div>
                                        <div>
                                            <span>Paid</span>
                                            <strong>${formatMoney(payoutBalances.totalWithdrawn)}</strong>
                                        </div>
                                    </div>
                                    <div className="profile-withdrawal-form">
                                        <label>
                                            Amount
                                            <input
                                                type="number"
                                                min="1"
                                                step="0.01"
                                                value={withdrawalAmount}
                                                onChange={(event) => setWithdrawalAmount(event.target.value)}
                                                placeholder={formatMoney(payoutBalances.withdrawableBalance)}
                                            />
                                        </label>
                                        <button
                                            className="btn-primary"
                                            onClick={handleRequestWithdrawal}
                                            disabled={withdrawalLoading || payoutBalances.withdrawableBalance < 1}
                                        >
                                            {withdrawalLoading ? 'Requesting...' : 'Request payout'}
                                        </button>
                                    </div>
                                    {withdrawalMessage && <div className="profile-withdrawal-message">{withdrawalMessage}</div>}
                                </section>
                                <section className="profile-settings-card">
                                    <div className="profile-settings-card-header">
                                        <div>
                                            <span className="profile-settings-eyebrow">Store</span>
                                            <h3>Buyer view</h3>
                                        </div>
                                    </div>
                                    <p>Preview your public marketplace listing with the current look.</p>
                                    <button className="btn-secondary" onClick={() => navigate(`/marketplace?artist=${encodeURIComponent(displayName || '')}`)}>View Store</button>
                                </section>
                            </div>
                        </div>
                    )}

                    {activeTab === 'activity' && (
                        <div className="activity-section-modern">
                            <h3 className="section-title-modern">Recent Activity</h3>
                            {recentActivity.length === 0 ? (
                                <div className="empty-state-modern">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                                    </svg>
                                    <h3>No activity yet</h3>
                                    <p>Your recent activity will appear here</p>
                                </div>
                            ) : (
                                <div className="activity-list">
                                    {recentActivity.map((activity, index) => (
                                        <div key={index} className="activity-item">
                                            <div className="activity-icon">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"/>
                                                    <polyline points="12 6 12 12 16 14"/>
                                                </svg>
                                            </div>
                                            <div className="activity-content">
                                                <p>{activity}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;
